// @flow

import * as React from 'react';

import {
  selectCartIsActive,
  selectCartCurrency,
  type ProductId,
} from '@gandi/cart';
import { Select } from '@gandi/form';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { useBestCatalogPrice } from '../../../modules/catalog';
import type { GandiFormSelectOption } from '../../types';

export type Props = {
  disabled: boolean,
  onChange?: (value: GandiFormSelectOption<number>) => mixed,
  value?: number,
  id: string,
  unit: 'm' | 'y',
  values: Array<number>,
  readOnly?: boolean,
  productId: ProductId,
};

export default function DurationSelect({
  disabled,
  onChange,
  value,
  id,
  unit,
  values,
  readOnly,
  productId,
}: Props): React.MixedElement {
  const { __, formatPrice } = useTranslator();
  const cartActive = useAppSelector(selectCartIsActive);

  const currency = useAppSelector(selectCartCurrency);

  const { getBestPrice } = useBestCatalogPrice(productId);

  const options: Array<GandiFormSelectOption<number>> = values.map(
    optionValue => {
      const correspondingPrice = getBestPrice(optionValue, unit);

      if (correspondingPrice) {
        return {
          text:
            unit === 'y'
              ? __('%(count)s years - %(price)s', {
                  count: String(optionValue),
                  price: formatPrice(
                    correspondingPrice.price_before_taxes *
                      parseInt(optionValue, 10),
                    currency,
                  ),
                })
              : __('%(count)s months - %(price)s', {
                  count: String(optionValue),
                  price: formatPrice(
                    correspondingPrice.price_before_taxes *
                      parseInt(optionValue, 10),
                    currency,
                  ),
                }),
          value: optionValue,
        };
      }
      return {
        text:
          unit === 'y'
            ? __('%(count)s years', { count: String(optionValue) })
            : __('%(count)s months', { count: String(optionValue) }),
        value: optionValue,
      };
    },
  );

  const selectedOption = options.find(opt => opt.value === value)?.value;

  const isDisabled = disabled || options.length === 0 || !cartActive;

  const onSelectChange: (SyntheticEvent<HTMLSelectElement>) => void = evt => {
    const newDuration = options.find(
      opt => opt.value === parseInt(evt.currentTarget.value, 10),
    );
    if (onChange && newDuration !== undefined) {
      onChange(newDuration);
    }
  };

  return (
    <Select
      disabled={isDisabled}
      id={id}
      onChange={e => onSelectChange(e)}
      value={selectedOption}
      values={options}
      readOnly={readOnly || (!disabled && options.length === 1)}
      variant={['small']}
      aria-label={__('Duration')}
    />
  );
}
